import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { RAIL_WIDTH, RAIL_WIDTH_CSS_VAR } from "../sidebar/railConstants";

/**
 * The chat pane is paid first out of the Codesmith container's width; the
 * side panels may only auto-open with what remains. Priority when space is
 * short: chat, then the left session sidebar, then the right details panel.
 */
export const MIN_CHAT_PANE_WIDTH = 640;

/**
 * App-nav rail width used to estimate the container before first measure.
 * Prefers the live width the mounted rail publishes (48px collapsed, 200px
 * pinned open; see RAIL_WIDTH_CSS_VAR); before the rail mounts, falls back
 * to its collapsed width.
 */
export function estimateAppNavRailWidth(): number {
  const published = Number.parseFloat(document.documentElement.style.getPropertyValue(RAIL_WIDTH_CSS_VAR));
  return Number.isFinite(published) ? published : RAIL_WIDTH;
}
export function canFitLeftSidebar({
  containerWidth,
  leftSidebarWidth,
  workspacePaneWidth = 0
}: {
  containerWidth: number;
  leftSidebarWidth: number;
  /** An open workspace pane's minimum footprint: the sidebar collapses
   *  before the pane, which auto-closes last (see useWorkspacePaneLayout). */
  workspacePaneWidth?: number;
}): boolean {
  return containerWidth - leftSidebarWidth - workspacePaneWidth >= MIN_CHAT_PANE_WIDTH;
}

/**
 * The right panel's budget is what remains after the chat minimum AND the
 * left sidebar's actual footprint (0 when collapsed), so shrinking closes
 * the right panel before it ever squeezes the left one.
 */
export function canFitRightSidebar({
  containerWidth,
  openLeftSidebarWidth,
  rightSidebarWidth
}: {
  containerWidth: number;
  openLeftSidebarWidth: number;
  rightSidebarWidth: number;
}): boolean {
  return containerWidth - openLeftSidebarWidth - rightSidebarWidth >= MIN_CHAT_PANE_WIDTH;
}

/**
 * The left sidebar's footprint for the right panel's budget. The desktop
 * sidebar animates its width closed (see SessionSidebar), so on collapse the
 * footprint outlives the collapsed flag: the width keeps counting until the
 * exit animation finishes (wire `onExitComplete` to the sidebar), keeping
 * the right panel from auto-opening mid-tween and crushing the chat pane
 * below its minimum. Expansion counts the full width immediately, which
 * only errs toward closing the right panel early.
 */
export function useBudgetedLeftSidebarWidth({
  isCollapsed,
  sidebarWidth
}: {
  isCollapsed: boolean;
  sidebarWidth: number;
}): {
  budgetedLeftSidebarWidth: number;
  onExitComplete: () => void;
} {
  const [exitSettled, setExitSettled] = useState(isCollapsed);
  // Reset while expanded so the next collapse waits for its own exit.
  useEffect(() => {
    if (!isCollapsed) setExitSettled(false);
  }, [isCollapsed]);
  // Guard against a completion landing after a quick re-expand: settling
  // while open would make the next collapse skip the wait entirely.
  const isCollapsedRef = useRef(isCollapsed);
  isCollapsedRef.current = isCollapsed;
  const onExitComplete = useCallback(() => {
    if (isCollapsedRef.current) setExitSettled(true);
  }, []);
  return {
    budgetedLeftSidebarWidth: isCollapsed && exitSettled ? 0 : sidebarWidth,
    onExitComplete
  };
}

/**
 * Live width of the Codesmith container (everything right of the app nav),
 * which is the real estate the chat pane and side panels share. Measured
 * with a ResizeObserver so app-nav expansion and window resizes both count;
 * falls back to a viewport-based estimate until the element is available.
 */
export function useCodesmithContainerWidth(container) {
  const $ = _c(3);
  const [measuredWidth, setMeasuredWidth] = useState(null);
  let t0;
  let t1;
  if ($[0] !== container) {
    t0 = () => {
      if (!container) {
        return;
      }
      setMeasuredWidth(container.getBoundingClientRect().width);
      const observer = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width !== undefined) {
          setMeasuredWidth(width);
        }
      });
      observer.observe(container);
      return () => observer.disconnect();
    };
    t1 = [container];
    $[0] = container;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  if (measuredWidth !== null) {
    return measuredWidth;
  }
  return typeof window === "undefined" ? Number.MAX_SAFE_INTEGER : window.innerWidth - estimateAppNavRailWidth();
}